import { useEffect, useMemo, useState } from 'react';
import { normalizeImg } from '@/lib/images';
import { creditsToUsd, formatCreditsLabel, formatUsd } from '@/utils/credits';
import { getMarketplaceCatalog, getOwnedItems, getCreditBalance, purchaseItem } from '@/lib/coreApi';

/** Types for marketplace */
interface CatalogItem {
  id: string;
  sku?: string | null;
  title: string;
  description?: string;
  previewUrl?: string;
  priceCredits?: number;
  kind?: string;
  meta?: Record<string, unknown>;
}

interface MarketplaceItem {
  id: string;
  sku: string | null;
  sourceId: string | null;
  name: string;
  description?: string;
  image_url?: string;
  costCredits: number;
  kind: string;
  meta: Record<string, unknown>;
  owned: boolean;
}

interface ItemSectionProps {
  title: string;
  items: MarketplaceItem[];
  busy: string | null;
  onBuy: (item: MarketplaceItem) => void;
}

function deriveSourceId(sku: string | null | undefined, kind: string): string | null {
  if (!sku || !kind) return null;
  const prefix = `${kind}:`;
  return sku.startsWith(prefix) ? sku.slice(prefix.length) : null;
}

function mapItem(item: CatalogItem, kind: string, ownedSet: Set<string> | undefined): MarketplaceItem {
  const sku = item.sku ?? null;
  return {
    id: item.id,
    sku,
    sourceId: deriveSourceId(sku, kind),
    name: item.title || 'Untitled',
    description: item.description,
    image_url: item.previewUrl,
    costCredits: item.priceCredits ?? 0,
    kind,
    meta: item.meta || {},
    owned: ownedSet?.has(item.id) || false,
  };
}

export default function TeacherMarketplace() {
  const [balance, setBalance] = useState<number>(0);
  const [badges, setBadges] = useState<MarketplaceItem[]>([]);
  const [avatars, setAvatars] = useState<MarketplaceItem[]>([]);
  const [themes, setThemes] = useState<MarketplaceItem[]>([]);
  const [borders, setBorders] = useState<MarketplaceItem[]>([]);
  const [digitalGoods, setDigitalGoods] = useState<MarketplaceItem[]>([]);
  const [aiTools, setAiTools] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const balanceUsd = useMemo(() => creditsToUsd(balance), [balance]);

  async function load() {
    setLoading(true);
    setError('');

    try {
      const [catalogResult, ownedResult, balanceResult] = await Promise.allSettled([
        getMarketplaceCatalog('teacher'),
        getOwnedItems(),
        getCreditBalance(),
      ]);

      if (catalogResult.status === 'rejected') {
        throw catalogResult.reason;
      }

      const catalogRes = catalogResult.value;
      const ownedRes = ownedResult.status === 'fulfilled' ? ownedResult.value : null;
      const balanceRes = balanceResult.status === 'fulfilled' ? balanceResult.value : null;

      const sections: any = catalogRes?.data?.sections || {};
      const ownedIds = new Set<string>(
        (Array.isArray(ownedRes?.data) ? ownedRes.data : []).map((row: { item_id: string }) => row.item_id),
      );
      setBalance(Number(balanceRes?.data?.balance_credits ?? 0));
      setBadges((sections.badges || []).map((item: CatalogItem) => mapItem(item, 'badge', ownedIds)));
      setAvatars((sections.avatars || []).map((item: CatalogItem) => mapItem(item, 'avatar', ownedIds)));
      setThemes((sections.themes || []).map((item: CatalogItem) => mapItem(item, 'theme', ownedIds)));
      setBorders((sections.borders || []).map((item: CatalogItem) => mapItem(item, 'border', ownedIds)));
      setDigitalGoods(
        (sections.digital_goods || []).map((item: CatalogItem) => mapItem(item, item.kind || 'digital_good', ownedIds)),
      );
      setAiTools((sections.ai_tools || []).map((item: CatalogItem) => mapItem(item, 'ai_tool', ownedIds)));
    } catch (err: unknown) {
      console.error('[TeacherMarketplace] load failed', err);
      setError((err as Error)?.message || 'Unable to load marketplace');
      setBalance(0);
      setBadges([]);
      setAvatars([]);
      setThemes([]);
      setBorders([]);
      setDigitalGoods([]);
      setAiTools([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const buy = async (item: MarketplaceItem) => {
    setBusy(item.id);
    setError('');
    setMessage('');
    try {
      const result = await purchaseItem({ item_id: item.id, quantity: 1 });
      if (result?.data?.ok) {
        setMessage(`Unlocked: ${item.name}`);
      }
      await load();
    } catch (err: unknown) {
      console.error('[TeacherMarketplace] purchase failed', err);
      setError((err as Error)?.message || 'Unable to complete purchase');
    } finally {
      setBusy(null);
    }
  };

  const hasItems =
    badges.length > 0 ||
    avatars.length > 0 ||
    themes.length > 0 ||
    borders.length > 0 ||
    digitalGoods.length > 0 ||
    aiTools.length > 0;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Teacher Marketplace</h1>
        <p className="text-sm text-cocoa/70">Tower Credits are platform credits and cannot be redeemed off-platform.</p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/80 p-4 shadow-soft">
        <div className="text-sm text-cocoa/70">Tower balance</div>
        <div className="text-2xl font-semibold text-cocoa">
          {loading ? (
            '—'
          ) : (
            <>
              {formatCreditsLabel(balance)}
              <span className="block text-sm font-normal text-cocoa/60">≈ {formatUsd(balanceUsd)}</span>
            </>
          )}
        </div>
      </div>

      {message ? (
        <div className="rounded-xl border border-mint/40 bg-mint/10 px-4 py-3 text-sm text-cocoa/80">{message}</div>
      ) : null}
      {error ? <div className="rounded-xl border border-brick/40 bg-brick/10 px-4 py-3 text-sm text-brick">{error}</div> : null}

      {loading ? (
        <p className="text-sm text-cocoa/70">Loading marketplace…</p>
      ) : (
        <div className="space-y-8">
          {badges.length > 0 && (
            <ItemSection title="Badges" items={badges} busy={busy} onBuy={buy} />
          )}
          {avatars.length > 0 && (
            <ItemSection title="Avatars" items={avatars} busy={busy} onBuy={buy} />
          )}
          {themes.length > 0 && (
            <ItemSection title="Themes" items={themes} busy={busy} onBuy={buy} />
          )}
          {borders.length > 0 && (
            <ItemSection title="Borders" items={borders} busy={busy} onBuy={buy} />
          )}
          {digitalGoods.length > 0 && (
            <ItemSection title="Digital goods" items={digitalGoods} busy={busy} onBuy={buy} />
          )}
          {aiTools.length > 0 && (
            <ItemSection title="AI tools" items={aiTools} busy={busy} onBuy={buy} />
          )}

          {hasItems ? null : (
            <div className="rounded-3xl border border-dashed border-sand bg-white/70 p-6 text-center text-sm text-cocoa/70">
              No marketplace items are available right now.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ItemSection({ title, items, busy, onBuy }: ItemSectionProps) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-cocoa">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <div key={item.id} className="flex h-full flex-col justify-between rounded-3xl bg-white/80 p-5 shadow-soft">
            <div className="space-y-3">
              {item.image_url ? (
                <img
                  src={normalizeImg(item.image_url)}
                  alt={item.name}
                  className="h-16 w-16 rounded-2xl border border-shell/70 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-shell/70 text-xs text-cocoa/70">
                  {item.kind || 'item'}
                </div>
              )}
              <div>
                <div className="text-lg font-semibold text-cocoa">{item.name}</div>
                <p className="text-sm text-cocoa/70">{item.description}</p>
                {item.meta?.slot ? (
                  <div className="mt-1 text-xs text-cocoa/60">Slot: {String(item.meta.slot)}</div>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-cocoa">
                {formatCreditsLabel(item.costCredits ?? 0)}
                <span className="ml-1 text-xs font-normal text-cocoa/60">(≈ {formatUsd(creditsToUsd(item.costCredits ?? 0))})</span>
              </div>
              <button
                type="button"
                onClick={() => onBuy(item)}
                disabled={busy === item.id || item.owned}
                className="inline-flex min-w-[6rem] justify-center rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:cursor-not-allowed disabled:opacity-60"
              >
                {item.owned ? 'Owned' : busy === item.id ? 'Purchasing…' : 'Buy'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
